import { Component, ViewChild, ElementRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { ToastrService } from 'ngx-toastr';

import { CustomValidator } from '../../../../services/custom-validator/custom-validator.service';
import { SearchServiceStatusService } from '../../../../services/search-service-status/search-service-status.service';
import { SearchHistoryService } from '../../../../services/search-history/search-history.service';
import { FrameGeneratorComponent } from '../frame-generator/frame-generator.component';
import { ScannedResultsComponent } from '../scanned-results/scanned-results.component';
import { PaginatorComponent } from '../../../common/paginator/paginator.component';

@Component({
  selector: 'app-video-uploader',
  imports: [
    CommonModule,
    FrameGeneratorComponent,
    ScannedResultsComponent,
    PaginatorComponent,
  ],
  templateUrl: './video-uploader.component.html',
  styleUrl: './video-uploader.component.css',
})
export class VideoUploaderComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  uploadedVideo: any = null;
  showResult: any = false;
  isDragOver = false;
  MAX_VIDEO_DURATION: number = 600;
  scanCreditCost: number | null = null;

  loadingScannerHistory = false;
  scannerHistoryRows: any[] = [];
  scannerHistoryCount = 0;
  scannerHistoryPerPage = 5;
  scannerHistoryCurrentPage = 1;

  get scannerHistoryPagedStartAndEnd() {
    if (!this.scannerHistoryRows?.length || !this.scannerHistoryCount) {
      return { start: 0, end: 0 };
    }
    const start =
      (this.scannerHistoryCurrentPage - 1) * this.scannerHistoryPerPage + 1;
    const end = Math.min(
      start + this.scannerHistoryRows.length - 1,
      this.scannerHistoryCount,
    );
    return { start, end };
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private searchServiceStatusService: SearchServiceStatusService,
    private searchHistoryService: SearchHistoryService,
  ) {}

  ngOnInit(): void {
    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.scanCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'videoScannerCreditCost',
      );
    });
    this.loadScannerHistory();
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onShowResult() {
    this.showResult = true;
  }

  onChangeVideo(): void {
    this.uploadedVideo = null;
    this.showResult = false;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.handleFile(input.files[0]);
      input.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      this.handleFile(file);
    }
  }

  loadScannerHistory(): void {
    this.loadingScannerHistory = true;
    this.searchHistoryService
      .getVideoScannerHistories(
        this.scannerHistoryCurrentPage,
        this.scannerHistoryPerPage,
      )
      .subscribe({
        next: (response) => {
          this.scannerHistoryRows = response?.data?.data_list || [];
          this.scannerHistoryCount = response?.data?.total_items || 0;
          this.loadingScannerHistory = false;
        },
        error: (error) => {
          console.error(error);
          this.loadingScannerHistory = false;
          this.toastr.error(
            error?.error?.message_key ||
              error?.error?.message ||
              'Failed to load video scanner history',
            'Load History Failed',
          );
        },
      });
  }

  onScannerHistoryPageChange(page: number): void {
    this.scannerHistoryCurrentPage = page;
    this.loadScannerHistory();
  }

  openScannerHistoryResult(row: any): void {
    if (!row?.video_scanner_id) {
      return;
    }

    this.router.navigate(['/osint-image-search/video-scanner/results'], {
      queryParams: { video_scanner_id: row.video_scanner_id },
      state: {
        isStaleResult: true,
        videoScannerId: row.video_scanner_id,
        videoScannerHistory: row,
      },
    });
  }

  formatStatus(status: any): string {
    if (!status) return '—';
    return String(status).replace(/_/g, ' ');
  }

  isHistoryDone(row: any): boolean {
    return String(row?.status || '').toLowerCase() === 'done';
  }

  private handleFile(file: File): void {
    CustomValidator.validateVideoFile(file).then((errors: any) => {
      if (errors) {
        const errorMessage = Object.values(errors)[0];
        this.toastr.error(errorMessage as string);
        return;
      }

      const url = URL.createObjectURL(file);
      this.uploadedVideo = {
        file,
        name: file.name,
        size: file.size,
        url,
      };

      const videoElement = document.createElement('video');
      videoElement.src = url;
      videoElement.preload = 'metadata';
      videoElement.onloadedmetadata = () => {
        const duration = videoElement.duration;
        if (duration > this.MAX_VIDEO_DURATION) {
          this.uploadedVideo = null;
          this.toastr.error(
            `Video length should not exceed ${
              this.MAX_VIDEO_DURATION / 60
            } minutes`,
          );
          return;
        }
      };
    });
  }
}
